// Journal — notas/diario por día. Persistencia local + sync backend
import { Link, useNavigate } from "react-router-dom";
import { ChevronLeft, Plus, Trash2 } from "lucide-react";
import { useState, useEffect } from "react";
import axios from "axios";
import { TRIP as STATIC_TRIP } from "../data/trip";
import { formatDate } from "../data/helpers";
import { useTripBase } from "../hooks/useTripBase";
import { useTrip } from "../auth/TripContext";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const STORAGE_KEY = "viaje2026_journal_v1";

function loadLocal() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveLocal(map) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {}
}

export default function Journal() {
  const base = useTripBase();
  const { trip } = useTrip();
  const T = trip || {};
  const days = T.days || [];
  const [entries, setEntries] = useState(loadLocal); // { dayId: [ {id, text, created_at} ] }
  const [selectedDay, setSelectedDay] = useState(days[0]?.id || "");
  const [draft, setDraft] = useState("");
  const navigate = useNavigate();

  const dayEntries = entries[selectedDay] || [];

  const addEntry = () => {
    if (!draft.trim()) return;
    const e = {
      id: Date.now().toString(),
      text: draft.trim(),
      created_at: new Date().toISOString(),
    };
    const next = { ...entries, [selectedDay]: [...(entries[selectedDay] || []), e] };
    setEntries(next);
    saveLocal(next);
    setDraft("");
    axios.post(`${API}/journal`, { id: e.id, day_id: selectedDay, text: e.text, created_at: e.created_at }).catch(() => {});
  };

  const removeEntry = (id) => {
    const next = { ...entries, [selectedDay]: (entries[selectedDay] || []).filter((e) => e.id !== id) };
    setEntries(next);
    saveLocal(next);
  };

  const day = days.find((d) => d.id === selectedDay);

  return (
    <div className="container-app pt-6 pb-28" data-testid="journal-page">
      <Link to={base || "/"} className="inline-flex items-center gap-1 text-sm text-[hsl(var(--muted))] mb-4" data-testid="journal-back">
        <ChevronLeft size={16} /> Inicio
      </Link>
      <h1 className="text-3xl font-bold leading-tight">Diario</h1>
      <p className="text-sm text-[hsl(var(--muted))] mt-1 mb-5">Tus notas y memorias del viaje — guardadas en tu iPhone</p>

      {/* Selector de día */}
      <div className="flex gap-2 overflow-x-auto hide-scrollbar pb-2 mb-3">
        {days.map((d) => (
          <button
            key={d.id}
            onClick={() => setSelectedDay(d.id)}
            className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
              selectedDay === d.id
                ? "bg-[hsl(var(--ink))] text-[hsl(var(--bg))]"
                : "bg-[hsl(var(--bg-card))] text-[hsl(var(--ink))] border border-[hsl(var(--line))]"
            }`}
            data-testid={`journal-day-${d.id}`}
          >
            {formatDate(d.date)} · {d.city.split(" ")[0]}
          </button>
        ))}
      </div>

      {day && (
        <div className="card-paper p-3 mb-4">
          <p className="text-[11px] uppercase tracking-wider text-[hsl(var(--muted))] font-semibold">{formatDate(day.date)} · {day.dayLabel}</p>
          <p className="font-semibold text-[hsl(var(--ink))]">{day.title}</p>
        </div>
      )}

      {/* Add note */}
      <div className="card-paper p-4 mb-5">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={4}
          placeholder="¿Cómo estuvo el día? Qué probaron, qué los emocionó, alguna anécdota..."
          className="w-full p-3 rounded-xl border border-[hsl(var(--line))] text-sm bg-white serif resize-none"
          data-testid="journal-input"
        />
        <button onClick={addEntry} disabled={!draft.trim()} className="btn-primary mt-3 w-full flex items-center justify-center gap-2 disabled:opacity-40" data-testid="journal-add">
          <Plus size={16} /> Agregar nota
        </button>
      </div>

      {/* Entries */}
      {dayEntries.length === 0 ? (
        <p className="text-center text-sm text-[hsl(var(--muted))] py-8">Sin notas aún. Escribe la primera arriba.</p>
      ) : (
        <ul className="space-y-3">
          {dayEntries.map((e) => (
            <li key={e.id} className="card-paper p-4" data-testid={`journal-entry-${e.id}`}>
              <p className="text-[11px] uppercase tracking-wider text-[hsl(var(--muted))] mb-1">
                {new Date(e.created_at).toLocaleString("es-CL", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
              </p>
              <p className="serif text-[15px] text-[hsl(var(--ink))] leading-[1.6] whitespace-pre-wrap">{e.text}</p>
              <button onClick={() => removeEntry(e.id)} className="mt-2 text-xs text-[hsl(var(--muted))] inline-flex items-center gap-1" data-testid={`journal-delete-${e.id}`}>
                <Trash2 size={12} /> Eliminar
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
